/**
 * OTP (One-Time Password) model interfaces matching the OTP API specification
 * 
 * @remarks
 * These models correspond to the OTP API endpoints at `/api/otp`
 * Supports email and SMS delivery methods with rate limiting and auto-expiration.
 * 
 * @see OTP_API_REFERENCE.md for complete API documentation
 */

import { BaseEntity } from './base';

/**
 * OTP delivery method
 */
export type OtpDeliveryMethod = 'EMAIL' | 'SMS';

/**
 * OTP status
 */
export type OtpStatus = 'PENDING' | 'VERIFIED' | 'EXPIRED' | 'FAILED';

/**
 * OTP purpose/context
 */
export type OtpPurpose = 'registration' | 'login' | 'password-reset' | string;

/**
 * Data Transfer Object for sending OTP
 * 
 * @remarks
 * Used in POST /api/otp/send and POST /api/otp/resend
 * Rate limited to 5 requests per 15 minutes per IP
 */
export interface SendOtpDto {
  /**
   * Email address or phone number (required)
   * - Email format: `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`
   * - Phone format: `/^\+?[\d\s\-()]{10,}$/`
   */
  recipient: string;

  /**
   * Delivery method (required)
   */
  deliveryMethod: OtpDeliveryMethod;

  /**
   * Purpose/context of OTP (optional)
   * Common values: 'registration', 'login', 'password-reset'
   */
  purpose?: OtpPurpose;

  /**
   * OTP code length (optional)
   * @default 6
   * @min 6
   * @max 8
   */
  otpLength?: number;
}

/**
 * Data Transfer Object for verifying OTP
 * 
 * @remarks
 * Used in POST /api/otp/verify
 * Rate limited to 10 requests per 15 minutes per IP
 * Maximum 5 verification attempts per OTP
 */
export interface VerifyOtpDto {
  /**
   * Email or phone number (required)
   * Must match the recipient used in send request
   */
  recipient: string;

  /**
   * 6-8 digit OTP code (required)
   * Must be numeric digits only
   */
  code: string;

  /**
   * Purpose/context (optional)
   * Must match if provided during send
   */
  purpose?: OtpPurpose;
}

/**
 * Query parameters for getting OTP status
 * 
 * @remarks
 * Used in GET /api/otp/status/:recipient
 */
export interface GetOtpStatusQueryParams {
  /**
   * Filter by purpose (optional)
   */
  purpose?: OtpPurpose;
}

/**
 * OTP data returned after sending
 */
export interface OtpSentData {
  /**
   * Recipient email or phone number
   */
  recipient: string;

  /**
   * Delivery method used
   */
  deliveryMethod: OtpDeliveryMethod;

  /**
   * Expiration timestamp (ISO 8601)
   * OTPs expire 10 minutes after creation
   */
  expiresAt: string;

  /**
   * OTP record ID (MongoDB ObjectId)
   */
  otpId: string;
}

/**
 * OTP verification result data
 */
export interface OtpVerifiedData {
  /**
   * Verification success flag
   */
  verified: boolean;

  /**
   * OTP record ID
   */
  otpId: string;

  /**
   * Verification timestamp (ISO 8601)
   */
  verifiedAt: string;
}

/**
 * OTP status data
 */
export interface OtpStatusData {
  /**
   * OTP record ID (MongoDB ObjectId)
   */
  otpId: string;

  /**
   * Recipient email or phone number
   */
  recipient: string;

  /**
   * Delivery method used
   */
  deliveryMethod: OtpDeliveryMethod;

  /**
   * Current status
   */
  status: OtpStatus;

  /**
   * Number of verification attempts made
   */
  attempts: number;

  /**
   * Maximum allowed verification attempts
   * @default 5
   */
  maxAttempts: number;

  /**
   * Expiration timestamp (ISO 8601)
   * OTPs expire 10 minutes after creation
   */
  expiresAt: string;

  /**
   * Purpose/context (if provided)
   */
  purpose?: OtpPurpose;

  /**
   * Creation timestamp (ISO 8601)
   */
  createdAt: string;

  /**
   * Verification timestamp (ISO 8601, only if verified)
   */
  verifiedAt?: string;
}

/**
 * API response wrapper for OTP send operation
 */
export interface SendOtpResponse {
  status: 'success';
  message: string; // e.g., "OTP sent successfully via EMAIL"
  data: OtpSentData;
}

/**
 * API response wrapper for OTP verification success
 */
export interface VerifyOtpSuccessResponse {
  status: 'success';
  message: 'OTP verified successfully';
  data: OtpVerifiedData;
}

/**
 * API response wrapper for OTP verification failure
 */
export interface VerifyOtpFailureResponse {
  status: 'error';
  message: string; // e.g., "Invalid OTP code. 3 attempts remaining."
}

/**
 * Combined OTP verification response type
 */
export type VerifyOtpResponse = VerifyOtpSuccessResponse | VerifyOtpFailureResponse;

/**
 * API response wrapper for OTP status
 */
export interface OtpStatusResponse {
  status: 'success';
  data: OtpStatusData;
}

/**
 * API response wrapper for OTP not found
 */
export interface OtpNotFoundResponse {
  status: 'error';
  message: 'No OTP found for this recipient';
}

/**
 * Error response with validation details
 */
export interface OtpValidationError {
  status: 'error';
  message: string;
}

/**
 * Rate limit exceeded error
 */
export interface OtpRateLimitError {
  status: 'error';
  message: 'Too many requests. Please try again later.';
}

/**
 * Server error response
 */
export interface OtpServerError {
  status: 'error';
  message: 'Internal server error';
}

/**
 * Parsed verification error with extracted attempts count
 * 
 * @remarks
 * When verification fails, this provides structured error information
 * including remaining attempts parsed from the error message.
 */
export interface OtpVerificationError {
  /**
   * Original error message
   */
  message: string;

  /**
   * Remaining verification attempts (if parseable from message)
   */
  attemptsRemaining?: number;

  /**
   * Whether OTP is locked due to max attempts exceeded
   */
  isLocked: boolean;

  /**
   * Original error response
   */
  originalError: VerifyOtpFailureResponse;
}

/**
 * Configuration options for OTP status polling
 */
export interface OtpPollingOptions {
  /**
   * Polling interval in milliseconds
   * @default 2000 (2 seconds)
   */
  interval?: number;

  /**
   * Maximum polling duration in milliseconds
   * @default 600000 (10 minutes - matches OTP expiration)
   */
  maxDuration?: number;

  /**
   * Stop polling on these statuses
   * @default ['VERIFIED', 'EXPIRED', 'FAILED']
   */
  stopOnStatus?: OtpStatus[];
}
